import { ConfirmDialogProps, useConfirmDialogStore } from "@mirage/store/store";
import { useCallback, useRef } from "react";

export const useConfirmDialog = (): UseConfirmDialogReturn => {
    const setConfirmDialog = useConfirmDialogStore((s) => s.setConfirmDialog);
    const updateConfirmDialog = useConfirmDialogStore((s) => s.updateConfirmDialog);

    const hideDialog = useCallback(() => {
        updateConfirmDialog({ open: false, onConfirmColor: undefined, onConfirmDisabled: false });
    }, [updateConfirmDialog]);

    const onConfirm = useRef<VoidFunction>(hideDialog);
    const onReject = useRef<VoidFunction>(hideDialog);

    const isConfirmed = useCallback(() => {
        return new Promise<boolean>((resolve) => {
            onConfirm.current = () => {
                resolve(true);
                hideDialog();
                onConfirm.current = hideDialog;
            };
            onReject.current = () => {
                resolve(false);
                hideDialog();
                onReject.current = hideDialog;
            };
            updateConfirmDialog({
                onConfirm: onConfirm.current,
                onReject: onReject.current,
            });
        });
    }, [hideDialog, updateConfirmDialog]);

    const showDialog = useCallback(
        (props: Omit<ConfirmDialogProps, "onReject" | "onConfirm" | "open">) => {
            setConfirmDialog({
                ...props,
                open: true,
                onConfirm: onConfirm.current,
                onReject: onReject.current,
            });
            return isConfirmed();
        },
        [setConfirmDialog, isConfirmed]
    );

    const changeState = useCallback(
        ({ onConfirmDisabled }: Pick<ConfirmDialogProps, "onConfirmDisabled">) => {
            updateConfirmDialog({ onConfirmDisabled });
        },
        [updateConfirmDialog]
    );

    return [showDialog, isConfirmed, changeState];
};

type UseConfirmDialogReturn = [
    (props: Omit<ConfirmDialogProps, "onReject" | "onConfirm" | "open">) => Promise<boolean>,
    () => Promise<boolean>,
    ({ onConfirmDisabled }: Pick<ConfirmDialogProps, "onConfirmDisabled">) => void
];
